import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PermissionService } from '../../core/services/permission.service';
import { PermissionBit } from '../../core/utils/permission-types';

type PermissionMode = 'any' | 'all';

@Directive({
  selector: '[emPermission]',
  standalone: true,
})
export class EmPermissionDirective implements OnInit, OnDestroy {
  private permissions: PermissionBit | PermissionBit[] | null = null;
  private mode: PermissionMode = 'any';
  private destroy$ = new Subject<void>();

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {}

  @Input() set emPermission(permissions: PermissionBit | PermissionBit[]) {
    if (permissions === undefined || permissions === null) {
      throw new Error('emPermission directive requires a permission value');
    }
    this.permissions = permissions;
    this.updateView();
  }

  @Input() set emPermissionMode(mode: PermissionMode) {
    this.mode = mode || 'any';
    this.updateView();
  }

  ngOnInit(): void {
    this.permissionService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(): void {
    this.viewContainer.clear();
    
    if (this.permissions && this.hasRequiredPermissions()) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }

  private hasRequiredPermissions(): boolean {
    if (!this.permissions) {
      return false;
    }

    if (Array.isArray(this.permissions)) {
      if (this.mode === 'all') {
        return this.permissionService.hasAllPermissions(this.permissions);
      } else {
        return this.permissionService.hasAnyPermission(this.permissions);
      }
    } else {
      return this.permissionService.hasPermission(this.permissions);
    }
  }
}